import React from 'react';
import { Download } from 'lucide-react';

// Helper function to escape CSV values
const escapeCSV = (value) => {
  if (value == null || value === '') return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const downloadCoupaJSON = (invoiceData) => {
  // Validation
  const errors = [];
  
  if (!invoiceData.supplier.number) {
    errors.push('Supplier Number is required');
  }
  
  if (!invoiceData['account-type'].name) {
    errors.push('Account Type (Chart of Accounts) is required');
  }
  
  // Check non-PO lines have account codes
  invoiceData['invoice-lines'].forEach((line, index) => {
    if (!line['order-line-num']) {
      if (!line.account || !line.account.code) {
        errors.push(`Line ${index + 1}: Non-PO invoice lines require account code`);
      } else {
        const segments = line.account.code.split('-');
        if (segments.length !== 20) {
          errors.push(`Line ${index + 1}: Account code must have exactly 20 segments`);
        }
      }
    }
  });
  
  if (errors.length > 0) {
    alert('❌ Validation Failed:\n\n' + errors.join('\n'));
    return;
  }
  
  // Create Coupa-compatible JSON
  const cleanData = {
    'invoice-date': invoiceData['invoice-date'],
    'invoice-number': invoiceData['invoice-number'],
    'due-date': invoiceData['due-date'],
    currency: invoiceData.currency,
    
    // ONLY supplier number (not full object)
    supplier: {
      number: invoiceData.supplier.number
    },
    
    'supplier-site': invoiceData['supplier-site'],
    'remit-to-address': invoiceData['remit-to-address'],
    'ship-to-address': invoiceData['ship-to-address'],
    'requester-email': invoiceData['requester-email'],
    'line-level-taxation': invoiceData['line-level-taxation'],
    'account-type': invoiceData['account-type'],
    'payment-terms': invoiceData['payment-terms'],
    'handling-amount': invoiceData['handling-amount'],
    'shipping-amount': invoiceData['shipping-amount'],
    'misc-amount': invoiceData['misc-amount'],
    'discount-amount': invoiceData['discount-amount'],
    'invoice-lines': [],
    'tax-lines': invoiceData['tax-lines'] || []
  };
  
  // Clean invoice lines (remove QuickBooks-only fields)
  if (invoiceData['invoice-lines']) {
    cleanData['invoice-lines'] = invoiceData['invoice-lines'].map(line => {
      const cleanLine = { ...line };
      delete cleanLine['custom-fields']; // Remove internal field
      delete cleanLine['item-type']; // Remove QuickBooks-only field
      return cleanLine;
    });
  }
  
  const dataStr = JSON.stringify(cleanData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `coupa_invoice_${invoiceData['invoice-number']}.json`;
  link.click();
};

export const downloadCoupaCSV = (invoiceData, pdfFile) => {

  // ✅ ADD VALIDATION (same as JSON)
  const errors = [];
  
  if (!invoiceData.supplier.number) {
    errors.push('Supplier Number is required');
  }
  
  if (!invoiceData['account-type'].name) {
    errors.push('Account Type (Chart of Accounts) is required');
  }
  
  // Check non-PO lines have account codes
  invoiceData['invoice-lines'].forEach((line, index) => {
    if (!line['order-line-num']) {
      if (!line.account || !line.account.code) {
        errors.push(`Line ${index + 1}: Non-PO invoice lines require account code`);
      } else {
        const segments = line.account.code.split('-');
        if (segments.length !== 20) {
          errors.push(`Line ${index + 1}: Account code must have exactly 20 segments`);
        }
      }
    }
  });
  
  if (errors.length > 0) {
    alert('❌ Validation Failed:\n\n' + errors.join('\n'));
    return;
  }
  // ✅ END VALIDATION

  // Calculate total charges
  const handlingAmount = (invoiceData['handling-amount'] && invoiceData['handling-amount'] !== '') ? parseFloat(invoiceData['handling-amount']) : '';
  const miscAmount = (invoiceData['misc-amount'] && invoiceData['misc-amount'] !== '') ? parseFloat(invoiceData['misc-amount']) : '';
  const shippingAmount = (invoiceData['shipping-amount'] && invoiceData['shipping-amount'] !== '') ? parseFloat(invoiceData['shipping-amount']) : '';
  const discountAmount = (invoiceData['discount-amount'] && invoiceData['discount-amount'] !== '') ? parseFloat(invoiceData['discount-amount']) : '';

  let csv = '';
  
  // HEADER 1: Invoice Header
  csv += 'Invoice,Invoice Number,Supplier Number,Invoice Date,Submit For Approval?,Handling Amount,Misc Amount,Shipping Amount,Discount Amount,Payment Terms,Requester Email,Requester Lookup Name,Chart of Accounts,Currency,Image Scan Filename,Ship to Id,Ship To Name,Ship To Attention,Ship To Street1,Ship To Street2,Ship To City,Ship To State,Ship To Postal Code,Ship to Country Code,Remit To Code,Original invoice number,Original invoice date,Is Credit Note,Attachment 1,Attachment 2,Attachment 3,Attachment 4,Attachment 5\n';
  
  // HEADER 2: Invoice Line Header
  csv += 'Invoice Line,Invoice Number,Supplier Number,Line Number,Description,Supplier Part Number,Auxiliary Part Number,Price,Quantity,Unit of Measure,PO Number,PO Line Number,Account Name,Account Segment 1,Account Segment 2,Account Segment 3,Account Segment 4,Account Segment 5,Account Segment 6,Account Segment 7,Account Segment 8,Account Segment 9,Account Segment 10,Account Segment 11,Account Segment 12,Account Segment 13,Account Segment 14,Account Segment 15,Account Segment 16,Account Segment 17,Account Segment 18,Account Segment 19,Account Segment 20\n';
  
  // HEADER 3: Invoice Tax Line Header
  csv += 'Invoice Tax Line,Invoice Line Number,Tax Amount,Tax Rate,Tax Code,Tax Rate Type\n';
  
  // DATA ROW 1: Invoice Data
  csv += `Invoice,${escapeCSV(invoiceData['invoice-number'])},${escapeCSV(invoiceData.supplier.number)},${escapeCSV(invoiceData['invoice-date'])},,${handlingAmount},${miscAmount},${shippingAmount},${discountAmount},${escapeCSV(invoiceData['payment-terms'])},${escapeCSV(invoiceData['requester-email'])},,"${escapeCSV(invoiceData['account-type'].name)}",${escapeCSV(invoiceData.currency.code)},${escapeCSV(pdfFile?.name)},${escapeCSV(invoiceData['ship-to-address'].id)},,,,,,,,,${escapeCSV(invoiceData['remit-to-address']['remit-to-code'])},,,,,,,,\n`;
  
  // DATA ROWS 2: Invoice Lines
  invoiceData['invoice-lines'].forEach(line => {
    const segments = line.account?.code?.split('-') || [];
    csv += `Invoice Line,${escapeCSV(invoiceData['invoice-number'])},${escapeCSV(invoiceData.supplier.number)},${escapeCSV(line['line-num'])},${escapeCSV(line.description)},${escapeCSV(line['supplier-part-number'])},${escapeCSV(line['auxiliary-part-number'])},${escapeCSV(line.price)},${escapeCSV(line.quantity)},${escapeCSV(line.uom.code)},${escapeCSV(line['po-number'])},${escapeCSV(line['order-line-num'])},,${escapeCSV(segments[0])},${escapeCSV(segments[1])},${escapeCSV(segments[2])},${escapeCSV(segments[3])},${escapeCSV(segments[4])},${escapeCSV(segments[5])},${escapeCSV(segments[6])},${escapeCSV(segments[7])},${escapeCSV(segments[8])},${escapeCSV(segments[9])},${escapeCSV(segments[10])},${escapeCSV(segments[11])},${escapeCSV(segments[12])},${escapeCSV(segments[13])},${escapeCSV(segments[14])},${escapeCSV(segments[15])},${escapeCSV(segments[16])},${escapeCSV(segments[17])},${escapeCSV(segments[18])},${escapeCSV(segments[19])}\n`;
  });
  
  // DATA ROWS 3: Tax Lines (one row per tax line across all invoice lines)
  invoiceData['invoice-lines'].forEach(line => {
    if (line['tax-lines'] && line['tax-lines'].length > 0) {
      line['tax-lines'].forEach(tax => {
        csv += `Invoice Tax Line,${escapeCSV(line['line-num'])},${escapeCSV(tax.amount)},${escapeCSV(tax.rate)},,\n`;
      });
    }
  });

  // DATA ROWS 4: Tax Lines at invoice level (if any)
  if (invoiceData['tax-lines'] && invoiceData['tax-lines'].length > 0) {
    invoiceData['tax-lines'].forEach(tax => {
      csv += `Invoice Tax Line,,${escapeCSV(tax.amount)},${escapeCSV(tax.rate)},,\n`;
    });
  }

  const dataBlob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `coupa_invoice_${invoiceData['invoice-number']}.csv`;
  link.click();
};

export const CoupaExportButtons = ({ invoiceData, pdfFile, exportFormat, setExportFormat }) => {
  return (
    <div className="space-y-3">
      {/* Export Format Selector */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
          Format:
        </label>
        <select
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value)}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="csv">CSV (Recommended)</option>
          <option value="json">JSON (API Integration)</option>
        </select>
      </div>

      {/* Download Buttons */}
      {exportFormat === 'json' && (
        <button
          onClick={() => downloadCoupaJSON(invoiceData)}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
        >
          <Download className="w-5 h-5" />
          Download Coupa JSON
        </button>
      )}

      {exportFormat === 'csv' && (
        <button
          onClick={() => downloadCoupaCSV(invoiceData, pdfFile)}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
        >
          <Download className="w-5 h-5" />
          Download Coupa CSV
        </button>
      )}
    </div>
  );
};